import React, { useState, useEffect } from 'react';
import { usePipelineContext } from '../contexts/PipelineContext';
import {
  Box,
  LinearProgress,
  Tooltip,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Stack,
  Paper,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  HourglassEmpty,
  PlayCircle,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';

interface PipelineStage {
  id: number;
  name: string;
  status: string;
  duration?: number;
  startedAt?: string;
  finishedAt?: string;
}

interface CompactPipelineProgressProps {
  pipelineId: string;
  domainId?: string;
  platform?: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
  initialStatus?: string;
  initialPhase?: string;
  /**
   * The FS build id this row represents. When supplied, the context-update
   * path only accepts updates whose buildId matches — preventing the newest
   * build for the same host+platform from leaking its status onto older rows
   * (which produced "Completed" green chips on rows whose real underlying
   * pipeline was still running).
   */
  buildId?: number | string;
}

// 7 phases in CI order: preinit(1/7) → npm_build(2/7) → … → fastlane(7/7)
const TOTAL_PHASES = 7;

// Progress percentage per phase: (step + 1) / TOTAL_PHASES * 100
const PROGRESS_MAP: Record<string, number> = {
  // Pre-start
  'queued':              0,
  'pending':             0,
  'initializing':        0,

  // Phase sequence (exact names from CI)
  'preinit':             Math.round((1 / TOTAL_PHASES) * 100), // 14%
  'npm_build':           Math.round((2 / TOTAL_PHASES) * 100), // 29%
  'npm_build_started':   Math.round((2 / TOTAL_PHASES) * 100),
  'fetch_api':           Math.round((3 / TOTAL_PHASES) * 100), // 43%
  'fetching_app_data':   Math.round((3 / TOTAL_PHASES) * 100),
  'download':            Math.round((4 / TOTAL_PHASES) * 100), // 57%
  'downloading_files':   Math.round((4 / TOTAL_PHASES) * 100),
  'decrypt':             Math.round((5 / TOTAL_PHASES) * 100), // 71%
  'decrypting_files':    Math.round((5 / TOTAL_PHASES) * 100),
  'update_configs':      Math.round((6 / TOTAL_PHASES) * 100), // 86%
  'updating_configs':    Math.round((6 / TOTAL_PHASES) * 100),
  'fastlane':            Math.round((7 / TOTAL_PHASES) * 100), // 100%
  'fastlane_started':    Math.round((7 / TOTAL_PHASES) * 100),
  'building':            Math.round((7 / TOTAL_PHASES) * 100),
  'archiving':           Math.round((7 / TOTAL_PHASES) * 100),
  'uploading':           Math.round((7 / TOTAL_PHASES) * 100),
  'cleaning_up':         Math.round((7 / TOTAL_PHASES) * 100),

  // Terminal
  'success':   100,
  'completed': 100,
  'failed':    0,
  'cancelled': 0,
  'canceled':  0,
};

// Get progress percentage from status and phase
const getProgressFromStatus = (status: string, phase?: string): number => {
  // If explicitly failed or success, use status
  const normStatus = status?.toLowerCase() || '';

  if (normStatus === 'success' || normStatus === 'completed') return 100;

  // Always prioritize phase if it exists and is mapped.
  if (phase) {
    const normPhase = phase.toLowerCase().replace(/\s+/g, '_');
    if (PROGRESS_MAP[normPhase]) return PROGRESS_MAP[normPhase];
  }

  // Fallback to status mapping
  const consolidatedKey = normStatus.replace(/\s+/g, '_');
  return PROGRESS_MAP[consolidatedKey] || 5;
};

// Get status display text
const getStatusDisplayText = (status: string, phase?: string): string => {
  const normStatus = status?.toLowerCase();

  if (normStatus === 'failed' && phase) {
    return `Failed: ${phase.replace(/_/g, ' ')}`;
  }

  if (normStatus === 'running' && phase) {
    return phase.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()); // Capitalize
  }

  // Fallback map
  const statusMap: Record<string, string> = {
    'queued': 'Running', // RENAMED as requested
    'running': 'Running',
    'success': 'Completed',
    'failed': 'Failed',
    'cancelled': 'Cancelled',
    'canceled': 'Cancelled',
    'npm_build': 'Building NPM',
    'fetch_api': 'Fetching API',
    'download': 'Downloading',
    'decrypt': 'Decrypting',
    'update_configs': 'Updating Configs',
    'fastlane': 'Fastlane Build',
  };

  const key = normStatus?.replace(/\s+/g, '_');
  return statusMap[key] || phase || status || 'Processing';
};

// Check if status is a failure state
const isFailedStatus = (status: string): boolean => {
  const normalizedStatus = status?.toLowerCase();
  return normalizedStatus?.includes('failed') ||
    normalizedStatus?.includes('aborted') ||
    normalizedStatus?.includes('cancelled') ||
    normalizedStatus?.includes('canceled');
};

export const CompactPipelineProgress: React.FC<CompactPipelineProgressProps> = ({
  pipelineId,
  domainId,
  platform,
  initialStatus,
  initialPhase,
  buildId,
}) => {
  const [pipelineStatus, setPipelineStatus] = useState<string>(initialStatus || '');
  const [pipelinePhase, setPipelinePhase] = useState<string>(initialPhase || '');
  const [stages, setStages] = useState<PipelineStage[]>([]);
  // setStages is kept for future stage-detail expansion; suppress lint via void ref
  const _setStages = setStages; void _setStages;
  const [expanded, setExpanded] = useState(false);
  // Refs to track live context and prevent stale prop overwrites
  const hasContextUpdate = React.useRef(false);
  const propsPipelineIdRef = React.useRef(pipelineId);

  // Check if this is a placeholder or not-started pipeline ID
  const isPlaceholder = pipelineId.startsWith('queued-');
  const isNotStarted = pipelineId.startsWith('not-started-');

  // Helper to get local stored progress
  const getStoredDeployment = () => {
    try {
      const stored = localStorage.getItem('deployedItems');
      if (stored) {
        const data = JSON.parse(stored);
        // Construct key to match TableComponent logic
        const key = domainId && platform ? `${domainId}:${platform.toLowerCase()}` : '';
        if (data[key]) {
          return data[key]; // Returns object with { timestamp, pipelineId, status }
        }
      }
    } catch (e) {
      console.error('Error reading stored progress:', e);
    }
    return null;
  };

  // Reset state when pipelineId or props change.
  useEffect(() => {
    const TERMINAL_SET = new Set(['success', 'failed', 'cancelled', 'canceled', 'manual']);
    const initialIsTerminal = !!initialStatus && TERMINAL_SET.has(initialStatus.toLowerCase());

    // Terminal initialStatus is ALWAYS authoritative — the parent has
    // confirmed the build reached a final state, so the chip must reflect
    // that immediately. The early-return below (which skips updates when
    // hasContextUpdate is set) used to swallow this, leaving the chip
    // stuck on "Running" even though the row's Phase + Pipeline Status
    // chips had already flipped to Failed/Success. Operators saw the
    // inconsistency until they refreshed the page.
    if (initialIsTerminal) {
      propsPipelineIdRef.current = pipelineId;
      hasContextUpdate.current = false;
      setPipelineStatus(initialStatus);
      if (initialPhase) setPipelinePhase(initialPhase);
      return;
    }

    // Non-terminal path: keep the previous behaviour of preferring
    // context-driven state when the pipelineId hasn't changed and we've
    // already received a context update — that's the "DB is stale, live
    // context is fresher" case.
    if (pipelineId === propsPipelineIdRef.current && hasContextUpdate.current) {
      return;
    }
    propsPipelineIdRef.current = pipelineId;
    hasContextUpdate.current = false; // Reset on new ID

    // Initial load logic
    const stored = getStoredDeployment();
    // Valid window: 30 minutes. If older, ignore the queued state.
    const isLocalQueued = stored && (Date.now() - stored.timestamp < 1800000);

    // FORCE QUEUED STATE if we have a recent local trigger
    if (isLocalQueued) {
      setPipelineStatus('queued');
      setPipelinePhase('Queued');
    }
    else if (initialStatus && initialStatus !== 'N/A' && initialStatus !== 'unknown') {
      setPipelineStatus(initialStatus);
      if (initialPhase) setPipelinePhase(initialPhase);
    } else if (isPlaceholder) {
      setPipelineStatus('queued');
      setPipelinePhase('Queued');
    }
  }, [pipelineId, initialStatus, initialPhase, domainId, platform]);

  // --- SYNC WITH GLOBAL CONTEXT (EVENT-DRIVEN) ---
  const { getLatestBuildForApp } = usePipelineContext();

  useEffect(() => {
    if (!domainId || domainId === 'N/A') return;

    // If this row's own status is already terminal, never let the context override it.
    // getLatestBuildForApp always returns the NEWEST build for a host+platform, which may
    // be a different currently-running build — applying that to completed rows causes stale
    // "Running" display on older success/failed rows for the same app.
    const TERMINAL_SET = new Set(['success', 'failed', 'cancelled', 'canceled', 'manual']);
    if (initialStatus && TERMINAL_SET.has(initialStatus.toLowerCase())) return;

    // Attempt to extract pure host if domainId format is "host:id"
    const host = domainId.includes(':') ? domainId.split(':')[0] : domainId;

    // Pass platform to ensure we only get updates for the correct OS
    const latest = getLatestBuildForApp(host, platform);

    if (latest) {
      // 0. STRICT BUILD-ID MATCH (when this row knows its own buildId).
      //    Without this gate, the activity feed renders one row per build
      //    but `getLatestBuildForApp` always returns the NEWEST build for
      //    the host+platform. An older running row would then pick up the
      //    newer successful build's status and display "Completed" — even
      //    while the actual underlying pipeline for that older row was
      //    still running. The Pipeline Status chip (which reads row data
      //    directly) showed "Running" at the same time, producing an
      //    inconsistent UI for the same row.
      if (buildId != null) {
        const rowBuildId = String(buildId);
        const ctxBuildId = String((latest as any).buildId ?? '');
        if (ctxBuildId && ctxBuildId !== rowBuildId) {
          return;
        }
      }

      // 1. STRICT PLATFORM CHECK
      if (platform && latest.os) {
        const p1 = platform.toLowerCase().trim();
        const p2 = latest.os.toLowerCase().trim();
        if (p1 !== p2 && p1 !== 'unknown' && p2 !== 'unknown') {
          return;
        }
      }

      // 2. STRICT HOST CHECK (FIX FOR KONRAD ISSUE)
      // Ensure we don't partial-match "konrad.dev" against "konrad.development..."
      // We assume `latest.composerHost` comes from the API response
      if (latest.composerHost && latest.composerHost.toLowerCase() !== host.toLowerCase()) {
        return;
      }

      // 3. REVERTED LAG PROTECTION LOGIC
      // Check if we have a pending local deployment
      const stored = getStoredDeployment();

      // If we have a local trigger record
      if (stored) {
        const triggerTime = stored.timestamp;
        const apiBuildTime = new Date(latest.dateTime).getTime();

        // STRICT CHECK: If the API build is OLDER than our trigger action, IGNORE IT.
        // This keeps the local "Queued" state active until the API reflects the new build.
        if (apiBuildTime < triggerTime) {
          setPipelineStatus('queued');
          setPipelinePhase('Queued');
          return;
        }
      }

      // 4. Update state if new valid data is available
      if (latest.status && latest.status !== 'unknown') {
        hasContextUpdate.current = true;

        let displayStatus = latest.status;
        let displayPhase = latest.phase;

        // Map early backend phases to "Queued" for cleaner UI
        if (displayPhase && ['fetch_api', 'initializing', 'preparing'].includes(displayPhase.toLowerCase().replace(/\s/g, '_'))) {
          if (latest.status !== 'failed' && latest.status !== 'cancelled') {
            displayStatus = 'queued';
            displayPhase = 'Queued';
          }
        }

        setPipelineStatus(displayStatus);
        if (displayPhase) {
          setPipelinePhase(displayPhase);
        }
      }
    }
  }, [domainId, getLatestBuildForApp, initialStatus, pipelineId, platform, buildId]);

  const getStatusIcon = (status: string) => {
    const iconProps = { fontSize: 'small' as const, sx: { width: 16, height: 16 } };
    switch (status.toLowerCase()) {
      case 'success':
        return <CheckCircle {...iconProps} sx={{ ...iconProps.sx, color: '#4caf50' }} />;
      case 'failed':
        return <Cancel {...iconProps} sx={{ ...iconProps.sx, color: '#f44336' }} />;
      case 'running':
        return <PlayCircle {...iconProps} sx={{ ...iconProps.sx, color: '#2196f3' }} />;
      default:
        return <HourglassEmpty {...iconProps} sx={{ ...iconProps.sx, color: '#ff9800' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    if (isFailedStatus(status)) return '#f44336';
    if (status === 'success') return '#4caf50';
    if (status === 'queued') return '#ff9800';
    if (getProgressFromStatus(status) > 5) return '#2196f3';
    return '#9e9e9e';
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const currentStage = stages.find((s) => s.status === 'running') || stages[stages.length - 1];
  const hasRunningStage = stages.some((s) => s.status === 'running');

  const displayProgress = getProgressFromStatus(pipelineStatus, pipelinePhase);
  const statusText = getStatusDisplayText(pipelineStatus, pipelinePhase);
  const isFailed = isFailedStatus(pipelineStatus);
  const isSuccess = pipelineStatus === 'success';


  if (pipelineStatus && stages.length === 0) {
    // ── Failure: red outlined box + red text ──────────────────────────
    if (isFailed) {
      return (
        <Box sx={{
          width: '100%',
          minWidth: 150,
          height: '26px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid #f44336',
          borderRadius: '13px',
          px: 1.5,
        }}>
          <Typography variant="caption" sx={{
            color: '#f44336',
            fontWeight: 700,
            fontSize: '0.68rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {statusText}
          </Typography>
        </Box>
      );
    }

    // ── Normal progress bar ───────────────────────────────────────────
    let barColor = '#1976d2'; // active phase — blue
    if (isSuccess) barColor = '#2e7d32';             // green
    else if (pipelineStatus === 'queued' || displayProgress === 0) barColor = '#1976d2'; // blue even while queuing

    const isQueuing = pipelineStatus === 'queued' || pipelinePhase === 'Queued' || pipelinePhase === 'Initializing';

    // Label: show the current phase name in Title Case, or terminal status text
    const phaseLabel = pipelinePhase
      ? pipelinePhase.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : 'Running';
    const labelText = isSuccess
      ? 'Completed'
      : isQueuing
        ? 'Running'
        : phaseLabel;

    return (
      <Box sx={{ width: '100%', minWidth: 160, height: '22px', display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', position: 'relative' }}>
          <LinearProgress
            variant={isQueuing ? 'indeterminate' : 'determinate'}
            value={isQueuing ? undefined : displayProgress}
            sx={{
              height: 16,
              borderRadius: '8px',
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: barColor,
                borderRadius: '8px',
                transition: 'transform 0.5s ease',
              },
              // indeterminate bar colour
              '& .MuiLinearProgress-bar1Indeterminate': { backgroundColor: barColor },
              '& .MuiLinearProgress-bar2Indeterminate': { backgroundColor: barColor },
            }}
          />
          <Typography sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '0.62rem',
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
            zIndex: 2,
            textShadow: '0 1px 3px rgba(0,0,0,0.35)',
            pointerEvents: 'none',
          }}>
            {labelText}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!pipelineStatus || (isNotStarted && !pipelineStatus)) {
    return (
      <Box sx={{
        width: '100%',
        minWidth: 150,
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: '0.875rem',
            fontWeight: 400,
          }}
        >
          {(!isNotStarted && pipelineId) ? 'Queued' : 'Yet to Trigger'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      minWidth: 200,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          cursor: 'pointer',
          p: 0.5,
          borderRadius: 1,
          width: '100%',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Tooltip title={currentStage?.name || 'Pipeline'}>
          <Box
            sx={hasRunningStage ? {
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
              animation: 'spin 2s linear infinite',
            } : {}}
          >
            {getStatusIcon(currentStage?.status || 'pending')}
          </Box>
        </Tooltip>

        <Box sx={{ flex: 1, minWidth: 80 }}>
          <LinearProgress
            variant="determinate"
            value={displayProgress}
            sx={{
              height: 6,
              borderRadius: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getStatusColor(currentStage?.status || 'pending'),
                borderRadius: 1,
              },
            }}
          />
        </Box>

        <Typography variant="caption" fontWeight={600} sx={{ minWidth: 35, textAlign: 'right' }}>
          {Math.round(displayProgress)}%
        </Typography>

        <IconButton size="small" sx={{ p: 0 }}>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 1.5,
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Stack spacing={1}>
            {stages.map((stage) => (
              <Box
                key={stage.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 0.75,
                  borderLeft: `3px solid ${getStatusColor(stage.status)}`,
                  pl: 1,
                  backgroundColor:
                    stage.status === 'running'
                      ? 'rgba(33, 150, 243, 0.05)'
                      : 'rgba(255, 255, 255, 0.02)',
                  borderRadius: 1,
                }}
              >
                <Box
                  sx={stage.status === 'running' ? {
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                    animation: 'spin 2s linear infinite',
                  } : {}}
                >
                  {getStatusIcon(stage.status)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" fontWeight={600}>
                    {stage.name}
                  </Typography>
                </Box>
                <Chip
                  label={stage.status}
                  size="small"
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    backgroundColor: getStatusColor(stage.status),
                    color: '#fff',
                  }}
                />
                {stage.duration !== undefined && (
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
                    {formatDuration(stage.duration)}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      </Collapse>
    </Box>
  );
};